import type { FC, ReactNode } from "react";
import { useCallback } from "react";

import clsx from "clsx";

import PlusIcon from "assets/icons/outlined/plus.svg?react";

import { Button } from "shared/uikit";
import { ButtonSizesEnum, ButtonVariantsEnum } from "shared/uikit/types";

import colors from "theme/colors";

import type { FieldOptionModel } from "./FieldOption";
import FieldOption from "./FieldOption";

import SelectBase from "../SelectBase";

interface FieldsProps {
	options: Partial<FieldOptionModel>[];
	active: number;
	allowAddMore?: boolean;
	onAddMore?: () => void;
	onChangeSelected?: (index: number) => void;
	loading?: boolean;
	footer?: ReactNode;
	wrapperClassName?: string;
	bordered?: boolean;
	noPadding?: boolean;
	variant?: ButtonVariantsEnum | "UNDERLINED";
	noWrap?: boolean;
}

const FieldsSelect: FC<FieldsProps> = ({
	options,
	active,
	allowAddMore = true,
	onAddMore,
	onChangeSelected,
	loading,
	footer,
	wrapperClassName,
	bordered = true,
	noPadding,
	variant = ButtonVariantsEnum.FILLED,
	noWrap
}) => {
	const handleAddMore = useCallback(() => {
		onAddMore && onAddMore();
	}, [onAddMore]);

	const handleChangeSelected = useCallback(
		(idx: number) => {
			onChangeSelected && onChangeSelected(idx);
		},
		[onChangeSelected]
	);

	return (
		<div
			className={clsx(
				"bg-white rounded-xl",
				!noPadding && "py-3 px-4",
				bordered && "border border-gray-100",
				wrapperClassName
			)}
		>
			<SelectBase
				options={options}
				noWrap={noWrap}
				addMore={
					allowAddMore ? (
						<Button
							size={ButtonSizesEnum.XS}
							variant={ButtonVariantsEnum.FILLED}
							onClick={handleAddMore}
							iconButton={{
								icon: <PlusIcon className="w-5 h-5 svg-paths:stroke-white" />,
								circular: true
							}}
							fillColor={colors.palette.colors.gray[400]}
							className="ml-2"
						/>
					) : undefined
				}
				optionComponent={props => (
					<FieldOption
						handleSelected={() => handleChangeSelected(props.idx)}
						active={active}
						loading={loading}
						variant={variant}
						{...props}
					/>
				)}
			/>
			{footer}
		</div>
	);
};

export default FieldsSelect;
